import dayjs from "dayjs";
import { cargarDescuentos, nuevaConfiguracionDescuentos, problemaPorcentaje } from "./descuentos";

export const PERMISO_CONFIGURAR_EVENTOS = 72;
export const RUTA_LISTADO = "/configuracion-eventos";

// Mismos topes que valida el backend (ConfiguracionEventoService).
export const DURACION_MAXIMA_EVENTO_DIAS = 31;
export const PRECIO_MAXIMO_EVENTO = 9999999.99;
export const MENSAJE_BLOQUEO_MAX = 300;
export const ITEMS_POR_PAGINA = 10;

export const codigoErrorEvento = (error) =>
  error?.graphQLErrors?.[0]?.extensions?.code || error?.graphQLErrors?.[0]?.code
  || error?.networkError?.result?.errors?.[0]?.extensions?.code || error?.networkError?.result?.errors?.[0]?.code || null;

// Las operaciones GraphQL con resultado obligatorio pueden responder HTTP 500
// y conservar el motivo de negocio dentro del cuerpo de la respuesta.
export const mensajeErrorEvento = (error, fallback = "No se pudo completar la operación.") =>
  error?.graphQLErrors?.[0]?.message || error?.networkError?.result?.errors?.[0]?.message || error?.message || fallback;

// Pasos del asistente, en el orden en que se recorren.
export const PASOS = [
  { id: "datos", titulo: "Datos del evento", ayuda: "Nombre y horario en que funciona" },
  { id: "productos", titulo: "Productos a la venta", ayuda: "Qué se vende y a qué precio" },
  { id: "menu", titulo: "Menú universitario", ayuda: "Platos del cronograma del día" },
  { id: "cobro", titulo: "Cobro e impresión", ayuda: "Medios de pago e impresora" },
  { id: "facturacion", titulo: "Facturación", ayuda: "Descuentos por aplicación y tipo de usuario" },
  { id: "autogestion", titulo: "App de autogestión", ayuda: "Qué se bloquea durante el evento" },
  { id: "revision", titulo: "Revisar y guardar", ayuda: "Controles finales" },
];

export const nuevoFormulario = () => ({
  id: null,
  nombre: "",
  inicio: "",
  fin: "",
  activo: true,
  productos: [],
  menuUniversitario: false,
  descuentosClientes: nuevaConfiguracionDescuentos(),
  menuModoPrecio: "COMPRADOR",
  menuUniversitarioPrecio: "",
  mediosPago: null,
  idComandera: "",
  bloqueaMenu: false,
  bloqueaCafeteria: false,
  bloqueaAppCompleta: false,
  mensajeBloqueo: "",
});

// "2026-09-23 08:00:00" o ISO -> valor de <input type="datetime-local">.
export const fechaInput = (value) => String(value || "").replace(" ", "T").slice(0, 16);

export const formatoFecha = (value) => (value ? dayjs(fechaInput(value)).format("DD/MM/YYYY HH:mm") : "—");

export const formatoPesos = (value) =>
  new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 2 })
    .format(Number(value || 0));

// Hora de pared argentina -> instante, sin depender de la zona del navegador.
const instanteArgentina = (value) => dayjs(`${fechaInput(value)}-03:00`);

// Estado mostrado en el listado. Compara en horario argentino.
export const estadoEvento = (evento, ahora = dayjs()) => {
  if (!evento.activo) return "Inactivo";
  const inicio = instanteArgentina(evento.inicioArgentina || evento.inicio);
  const fin = instanteArgentina(evento.finArgentina || evento.fin);
  if (ahora.isBefore(inicio)) return "Programado";
  if (ahora.isBefore(fin)) return "En curso";
  return "Finalizado";
};

// Terminado aunque esté desactivado: ya no se edita, se consulta o se duplica.
export const eventoFinalizado = (evento, ahora = dayjs()) =>
  !ahora.isBefore(instanteArgentina(evento.finArgentina || evento.fin));

// Mismo tope que el backend para un evento ya comenzado:
// max(max(inicio, ahora) + 31 días, fin guardado).
const topeCierreEnCurso = (inicio, finGuardado, ahora) => {
  const inicioEvento = instanteArgentina(inicio);
  const desde = inicioEvento.isAfter(ahora) ? inicioEvento : ahora;
  const tope = desde.add(DURACION_MAXIMA_EVENTO_DIAS, "day");
  const guardado = finGuardado ? instanteArgentina(finGuardado) : null;
  return guardado && guardado.isAfter(tope) ? guardado : tope;
};

// Nuevo cierre de un evento finalizado: posterior a ahora, sin tope de duración.
export const problemaNuevoCierre = (fin, ahora = dayjs()) => {
  if (!fin) return "Indique el nuevo cierre.";
  if (!instanteArgentina(fin).isAfter(ahora)) return "El nuevo cierre tiene que ser posterior a ahora.";
  return null;
};

// Valor mínimo para <input type="datetime-local">, en hora argentina.
export const ahoraInput = (ahora = dayjs()) => new Date(ahora.valueOf() - 3 * 60 * 60 * 1000).toISOString().slice(0, 16);

export const eventoComenzado = (evento, ahora = dayjs()) =>
  !ahora.isBefore(instanteArgentina(evento.inicioArgentina || evento.inicio));

export const duracionTexto = (inicio, fin) => {
  if (!inicio || !fin) return "";
  const minutos = dayjs(fin).diff(dayjs(inicio), "minute");
  if (!Number.isFinite(minutos) || minutos <= 0) return "";
  const dias = Math.floor(minutos / 1440);
  const horas = Math.floor((minutos % 1440) / 60);
  const resto = minutos % 60;
  return [
    dias ? `${dias} día${dias === 1 ? "" : "s"}` : "",
    horas ? `${horas} h` : "",
    resto ? `${resto} min` : "",
  ].filter(Boolean).join(" ");
};

// Un rango que cruza la medianoche deja POS y bloqueos activos de noche.
export const cruzaMedianoche = (inicio, fin) =>
  Boolean(inicio && fin) && dayjs(inicio).format("YYYY-MM-DD") !== dayjs(fin).subtract(1, "minute").format("YYYY-MM-DD");

export const desdeConfiguracion = (config) => {
  const { evento, productos } = config;
  const precioMenu = evento.menuUniversitarioPrecio;
  return {
    ...nuevoFormulario(),
    id: evento.id,
    nombre: evento.nombre || "",
    inicio: fechaInput(evento.inicioArgentina || evento.inicio),
    fin: fechaInput(evento.finArgentina || evento.fin),
    activo: evento.activo !== false,
    productos: (productos || []).map((item) => ({
      idProducto: String(item.idProducto),
      precioEvento: item.precioEvento ?? "",
      habilitado: item.habilitado !== false,
      producto: item.producto || null,
    })),
    menuUniversitario: Boolean(evento.menuUniversitario),
    descuentosClientes: cargarDescuentos(evento.descuentosClientes),
    menuModoPrecio: precioMenu == null ? "COMPRADOR" : "FIJO",
    menuUniversitarioPrecio: precioMenu ?? "",
    mediosPago: evento.mediosPago?.length ? [...evento.mediosPago] : null,
    idComandera: evento.idComandera ? String(evento.idComandera) : "",
    // Un bloqueo total guarda también los parciales; en el formulario se
    // muestran solo los que el usuario eligió.
    bloqueaAppCompleta: Boolean(evento.bloqueaAppCompleta),
    bloqueaMenu: !evento.bloqueaAppCompleta && Boolean(evento.bloqueaMenu),
    bloqueaCafeteria: !evento.bloqueaAppCompleta && Boolean(evento.bloqueaCafeteria),
    mensajeBloqueo: evento.mensajeBloqueo || "",
    // Versión leída: el backend rechaza el guardado si otro usuario la cambió.
    updatedAt: evento.updatedAt || null,
  };
};

// Copia todo menos el id y el horario, que el usuario debe volver a elegir.
export const duplicarConfiguracion = (config) => ({
  ...desdeConfiguracion(config),
  id: null,
  updatedAt: null,
  nombre: `${config.evento.nombre} (copia)`,
  inicio: "",
  fin: "",
});

export const aInput = (form) => ({
  id: form.id || null,
  nombre: form.nombre.trim(),
  inicio: form.inicio,
  fin: form.fin,
  activo: form.activo,
  menuUniversitario: form.menuUniversitario,
  descuentosClientes: { ...form.descuentosClientes, general: form.descuentosClientes.general == null || form.descuentosClientes.general === "" ? null : Number(form.descuentosClientes.general), aplicaciones: form.descuentosClientes.aplicaciones.map((g) => ({ ...g, porcentaje: g.porcentaje == null || g.porcentaje === "" ? null : Number(g.porcentaje), tipos: g.tipos.map((t) => ({ ...t, porcentaje: t.porcentaje == null || t.porcentaje === "" ? null : Number(t.porcentaje) })) })) },
  menuUniversitarioPrecio:
    form.menuUniversitario && form.menuModoPrecio === "FIJO" ? Number(form.menuUniversitarioPrecio) : null,
  bloqueaMenu: form.bloqueaAppCompleta || form.bloqueaMenu,
  bloqueaCafeteria: form.bloqueaAppCompleta || form.bloqueaCafeteria,
  bloqueaAppCompleta: form.bloqueaAppCompleta,
  mensajeBloqueo: form.mensajeBloqueo.trim() || null,
  idComandera: form.idComandera || null,
  mediosPago: form.mediosPago,
  productos: form.productos.map((item, orden) => ({
    idProducto: String(item.idProducto),
    orden,
    habilitado: item.habilitado,
    precioEvento: item.precioEvento === "" || item.precioEvento == null ? null : Number(item.precioEvento),
  })),
  updatedAt: form.updatedAt || null,
});

// Mensaje del problema de un importe, o null si es válido. No redondea.
const problemaImporte = (valor) => {
  const importe = Number(valor);
  if (valor === "" || valor == null || !Number.isFinite(importe) || importe <= 0) return "debe ser mayor que cero";
  if (importe > PRECIO_MAXIMO_EVENTO) return "no puede superar $ 9.999.999,99";
  if (Math.abs(Math.round(importe * 100) - importe * 100) > 1e-6) return "admite hasta dos decimales";
  return null;
};

// Precio que cobraría el POS: el del evento o, vacío, el de carta.
const sinPrecioEfectivo = (item) => (item.precioEvento === "" || item.precioEvento == null)
  && !(Number(item.producto?.precioCarta) > 0);

// Devuelve { campo: mensaje } con los errores del paso. Vacío = válido.
// contexto: { opciones, restricciones: { inicioBloqueado, ultimaVenta }, ahora }.
export const validarPaso = (paso, form, contexto = {}) => {
  const { opciones = null, restricciones = {}, ahora = dayjs() } = contexto;
  const errores = {};
  if (paso === "facturacion") {
    const config = cargarDescuentos(form.descuentosClientes);
    const valores = [config.general, ...config.aplicaciones.flatMap((g) => [g.porcentaje, ...g.tipos.map((t) => t.porcentaje)])];
    if (valores.some(problemaPorcentaje)) errores.descuentosClientes = "Los descuentos deben estar entre 0 y 100 y admitir hasta dos decimales.";
  }
  if (paso === "datos") {
    if (!form.nombre.trim()) errores.nombre = "Escriba un nombre para reconocer el evento.";
    if (!form.inicio) errores.inicio = "Indique cuándo empieza.";
    if (!form.fin) errores.fin = "Indique cuándo termina.";
    if (form.inicio && form.fin) {
      const fin = instanteArgentina(form.fin);
      if (!dayjs(form.fin).isAfter(dayjs(form.inicio))) {
        errores.fin = "El fin tiene que ser posterior al inicio.";
      } else if (restricciones.comenzado && fin.isAfter(topeCierreEnCurso(form.inicio, restricciones.finGuardado, ahora))) {
        // Ya comenzado: hasta 31 días desde ahora, o el fin guardado si es mayor.
        errores.fin = `El cierre puede extenderse hasta ${DURACION_MAXIMA_EVENTO_DIAS} días desde ahora.`;
      } else if (!restricciones.comenzado
        && dayjs(form.fin).diff(dayjs(form.inicio), "minute") > DURACION_MAXIMA_EVENTO_DIAS * 1440) {
        errores.fin = `El evento puede durar hasta ${DURACION_MAXIMA_EVENTO_DIAS} días; para períodos más largos cree un evento por tramo.`;
      } else if (!restricciones.inicioBloqueado && !fin.isAfter(ahora)) {
        // Alta o evento programado sin ventas: tiene que terminar en el futuro.
        errores.fin = "El fin ya pasó; indique un horario futuro.";
      } else if (restricciones.inicioBloqueado && fin.isBefore(ahora)) {
        errores.fin = "El fin no puede quedar en el pasado mientras el evento está en curso o tiene ventas.";
      } else if (restricciones.ultimaVenta && fin.isBefore(instanteArgentina(restricciones.ultimaVenta))) {
        errores.fin = `El fin no puede ser anterior a la última venta (${formatoFecha(restricciones.ultimaVenta)}).`;
      }
    }
  }
  if (paso === "productos") {
    const conPrecioInvalido = form.productos.find((item) =>
      item.precioEvento !== "" && item.precioEvento != null && problemaImporte(item.precioEvento));
    const aLaVentaSinPrecio = form.productos.find((item) => item.habilitado && sinPrecioEfectivo(item));
    if (conPrecioInvalido) {
      errores.productos = `El precio de ${conPrecioInvalido.producto?.nombre || "un producto"} ${problemaImporte(conPrecioInvalido.precioEvento)}.`;
    } else if (aLaVentaSinPrecio) {
      errores.productos = `${aLaVentaSinPrecio.producto?.nombre || "Un producto"} no tiene precio de carta: indique un precio para el evento o páuselo.`;
    }
  }
  if (paso === "menu" && form.menuUniversitario && form.menuModoPrecio === "FIJO") {
    const problema = problemaImporte(form.menuUniversitarioPrecio);
    if (problema) {
      errores.menuUniversitarioPrecio = form.menuUniversitarioPrecio === "" || Number(form.menuUniversitarioPrecio) <= 0
        ? "Indique un precio mayor que cero o elija la tarifa de cada comprador."
        : `El precio ${problema}.`;
    }
  }
  if (paso === "cobro") {
    if (Array.isArray(form.mediosPago) && form.mediosPago.length === 0) {
      errores.mediosPago = "Elija al menos un medio de pago.";
    }
    if (form.idComandera && opciones) {
      const elegida = (opciones.comanderas || []).find((item) => String(item.id) === String(form.idComandera));
      if (!elegida) errores.idComandera = "La impresora guardada ya no está disponible: elija otra.";
      else if (elegida.habilitado === false) errores.idComandera = "La impresora está deshabilitada: elija otra o use la predeterminada.";
    }
  }
  if (paso === "autogestion" && form.mensajeBloqueo.trim().length > MENSAJE_BLOQUEO_MAX) {
    errores.mensajeBloqueo = `El mensaje admite hasta ${MENSAJE_BLOQUEO_MAX} caracteres.`;
  }
  return errores;
};

export const productosHabilitados = (form) => form.productos.filter((item) => item.habilitado);

// El POS necesita algo para vender: productos generales o menú.
export const tieneCatalogo = (form) => productosHabilitados(form).length > 0 || form.menuUniversitario;

export const resumenBloqueo = (form) => {
  if (form.bloqueaAppCompleta) return "Toda la app queda suspendida";
  if (form.bloqueaMenu && form.bloqueaCafeteria) return "Sin pedidos de menú ni de cafetería";
  if (form.bloqueaMenu) return "Sin pedidos de menú";
  if (form.bloqueaCafeteria) return "Sin pedidos de cafetería";
  return "La app funciona normalmente";
};

export const MENSAJE_BLOQUEO_PREDETERMINADO = {
  app: "La autogestión no está disponible durante este evento.",
  menu: "Los pedidos de Menú no están disponibles durante este evento.",
  cafeteria: "Los pedidos de Cafetería no están disponibles durante este evento.",
};

export const describirVoucher = (voucher) => {
  const valor = voucher.valorPapel ?? voucher.presupuesto;
  if (voucher.tipo === "MONTO") return `${formatoPesos(valor)} por papel`;
  const destino = voucher.tipo === "PRODUCTO" ? voucher.producto : voucher.categoria;
  return `${valor} u. de ${destino || "—"} por papel`;
};
